import { addToast, Select, SelectOptionType } from '@octano/global-ui';
import {
  forwardRef,
  Ref,
  useCallback,
  useEffect,
  useImperativeHandle,
  useState,
  useMemo,
} from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { updateTeacherClassManagementConfig } from '../../../../api/requests/settings';
import { TranslationsKeys } from '../../../../locales/translations';
import { CommonSectionMethods } from '../interfaces/common-section';
import { TeacherClassManagementConfig } from '../interfaces/teacher-class-management.interface';
import useTeacherClassManagementConfig from '../hooks/useTeacherClassManagementConfig';

const prefix = 'teacherClassManagement';

type FormFields = {
  rescheduleClassAllowed: SelectOptionType;
  updateClassroomAllowed: SelectOptionType;
};

const TeacherClassManagementSection = (
  _props: unknown,
  ref: Ref<CommonSectionMethods>,
) => {
  const { t } = useTranslation(TranslationsKeys.CONFIGURATION_ACADEMIC);
  const { isLoading, config, refetch } = useTeacherClassManagementConfig();

  const { control, handleSubmit, reset } = useForm<FormFields>();
  const [isUpdating, setIsUpdating] = useState(false);

  const yesNoOptions = useMemo<SelectOptionType[]>(
    () => [
      { label: t(`${prefix}.disabled`), value: 0 },
      { label: t(`${prefix}.enabled`), value: 1 },
    ],
    [t],
  );

  const onSave = useCallback(
    async (values: FormFields) => {
      setIsUpdating(true);
      const payload: TeacherClassManagementConfig = {
        rescheduleClassAllowed: Boolean(values.rescheduleClassAllowed?.value),
        updateClassroomAllowed: Boolean(values.updateClassroomAllowed?.value),
      };
      const { error } = await updateTeacherClassManagementConfig(payload);
      if (error) {
        addToast({
          icon: 'error',
          text: t(`${prefix}.errorSavingChanges`),
          color: 'danger',
        });
      } else {
        await refetch();
      }
      setIsUpdating(false);
    },
    [t, refetch],
  );

  useImperativeHandle(ref, () => ({
    save: handleSubmit(onSave),
    validate: async () => {},
  }));

  useEffect(() => {
    if (config) {
      reset({
        rescheduleClassAllowed:
          yesNoOptions.find(
            (opt) => Boolean(opt.value) === config.rescheduleClassAllowed,
          ) ?? yesNoOptions[0],
        updateClassroomAllowed:
          yesNoOptions.find(
            (opt) => Boolean(opt.value) === config.updateClassroomAllowed,
          ) ?? yesNoOptions[0],
      });
    }
  }, [config, reset, yesNoOptions]);

  return (
    <form>
      <h2 className="text-primary fs-18 fw-600 text-uppercase mb-3 mt-5">
        {t(`${prefix}.title`)}
      </h2>
      <Select
        control={control}
        name="rescheduleClassAllowed"
        label={t(`${prefix}.rescheduleClassAllowed.label`)}
        options={yesNoOptions}
        isClearable={false}
        disabled={isLoading || isUpdating}
      />
      <Select
        control={control}
        name="updateClassroomAllowed"
        label={t(`${prefix}.updateClassroomAllowed.label`)}
        options={yesNoOptions}
        isClearable={false}
        disabled={isLoading || isUpdating}
      />
    </form>
  );
};

export default forwardRef(TeacherClassManagementSection);
